// src/pages/PageMenu/ProgramSettingsPage.jsx
// إعدادات البرنامج — بالتصميم الموحّد (ReportPage) ومكونات MUI، مع دعم الترجمة
// الإعدادات تُحفظ لكل نوع فاتورة على حدة

import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  Box, Paper, Typography, TextField, MenuItem, Switch, Autocomplete, Button, Chip,
  Alert, CircularProgress, InputAdornment,
} from "@mui/material";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DisplaySettingsOutlinedIcon from "@mui/icons-material/DisplaySettingsOutlined";
import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import ToggleOnOutlinedIcon from "@mui/icons-material/ToggleOnOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import axios from "../../axios";
import { ThemeProvider } from "@mui/material/styles";
import { ReportPage, fieldSx, INDIGO_BRAND } from "../../components/Reports/ReportKit";

// هذه الصفحة وحدها باللون النيلي (بقية النظام بألوان الهوية في BRAND)
const BRAND = INDIGO_BRAND;
const indigoTheme = (outer) => ({
  ...outer,
  palette: {
    ...outer.palette,
    primary: { ...outer.palette.primary, main: "#4F46E5", light: "#818CF8", dark: "#3730A3", contrastText: "#FFFFFF" },
  },
});

const BORDER = "#e5e7eb";

const EMPTY = {
  id: null,
  decimalPlaces: 2,
  isVatExempt: false,
  isPriceIncludesVAT: false,
  autoPrintAfterSave: false,
  autoApproveInvoices: false,
  printCopies: 1,
  defaultCustomerId: null,
  defaultPaymentMethodId: null,
  defaultSafeId: null,
  defaultBankId: null,
  defaultStockId: null,
  defaultTaxTypeId: null,
  allowNegativeSales: false,
  pricingPolicy: "",
  defaultPrinterName: "",
  allowedSecondaryPrinterCount: 2,
  secondaryPrinterNames: [],
};

const nameOf = (o) => o?.name || o?.nameAr || o?.arabicName || o?.nameEn || "";

function Section({ title, icon, color = BRAND.primary, action, children, sx }) {
  return (
    <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: "14px", overflow: "hidden", ...sx }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.25, borderBottom: `1px solid ${BORDER}`, bgcolor: "#fafbfc" }}>
        <Box sx={{
          width: 30, height: 30, borderRadius: "9px", display: "grid", placeItems: "center",
          bgcolor: `${color}1a`, color, "& svg": { fontSize: 18 },
        }}>{icon}</Box>
        <Typography sx={{ fontWeight: 800, fontSize: 15, flex: 1 }}>{title}</Typography>
        {action}
      </Box>
      <Box sx={{ p: { xs: 1.5, sm: 2 } }}>{children}</Box>
    </Paper>
  );
}

const Grid2 = ({ children }) => (
  <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0,1fr))" } }}>{children}</Box>
);

export default function ProgramSettingsPage() {
  const { t, i18n } = useTranslation();
  const tr = (key, fallback, opts) => t(key, { defaultValue: fallback, ...opts });
  const navigate = useNavigate();
  const companyId = parseInt(localStorage.getItem("companyId"), 10);
  const dir = i18n.dir ? i18n.dir(i18n.language) : "rtl";

  const [invoiceTypeId, setInvoiceTypeId] = useState(null);
  const [setting, setSetting] = useState(EMPTY);
  const [saved, setSaved] = useState(EMPTY);   // آخر نسخة محفوظة — لمعرفة التغييرات غير المحفوظة
  const [lists, setLists] = useState({ invoiceTypes: [], customers: [], payments: [], safes: [], banks: [], stocks: [], taxes: [], printers: [] });
  const [loadingLists, setLoadingLists] = useState(true);
  const [loadingType, setLoadingType] = useState(false);
  const [saving, setSaving] = useState(false);

  // ── القوائم + أول إعداد محفوظ (لتحديد النوع الافتراضي) ──
  useEffect(() => {
    if (!companyId) return;
    let alive = true;
    const data = (r) => (r.status === "fulfilled" && Array.isArray(r.value?.data) ? r.value.data : []);

    Promise.allSettled([
      axios.get("/InvoiceTypes", { params: { companyId } }),
      axios.get(`/Customer/by-company/${companyId}`),
      axios.get(`/PaymentMethod/by-company/${companyId}`),
      axios.get(`/Safe/by-company/${companyId}`),
      axios.get(`/Bank/by-company/${companyId}`),
      axios.get(`/Stock/by-company/${companyId}`),
      axios.get("/VatTypes", { params: { companyId } }),
      axios.get("/Printers"),
      axios.get("/ProgramSettings/by-company", { params: { companyId } }),
    ]).then(([inv, cust, pay, safe, bank, stock, tax, printers, first]) => {
      if (!alive) return;
      setLists({
        invoiceTypes: data(inv), customers: data(cust), payments: data(pay), safes: data(safe),
        banks: data(bank), stocks: data(stock), taxes: data(tax),
        printers: data(printers).map(p => (typeof p === "string" ? p : p?.name)).filter(Boolean),
      });
      const firstType = first.status === "fulfilled" ? first.value?.data?.invoiceTypeId : null;
      setInvoiceTypeId(firstType ?? data(inv)[0]?.invoiceTypeId ?? null);
      if ([inv, cust, pay, safe, bank, stock, tax].some(r => r.status === "rejected")) {
        toast.warning(tr("programSettings.messages.loadListsFailed", "تعذّر تحميل بعض القوائم"));
      }
    }).finally(() => alive && setLoadingLists(false));

    return () => { alive = false; };
  }, [companyId]); // tr ثابت عمليًا

  // ── إعدادات النوع المختار (أو قيم افتراضية إذا لم تُحفظ بعد) ──
  useEffect(() => {
    if (!companyId || invoiceTypeId == null) return;
    let alive = true;
    setLoadingType(true);
    axios.get("/ProgramSettings/by-company-and-type", { params: { companyId, invoiceTypeId } })
      .then(res => {
        const next = { ...EMPTY, ...(res.data || {}) };
        next.secondaryPrinterNames = Array.isArray(next.secondaryPrinterNames) ? next.secondaryPrinterNames : [];
        if (alive) { setSetting(next); setSaved(next); }
      })
      .catch(() => {
        // لا توجد إعدادات لهذا النوع — نبدأ من الافتراضي وبدون id حتى لا نعدّل إعدادات نوع آخر
        if (alive) { setSetting(EMPTY); setSaved(EMPTY); }
      })
      .finally(() => alive && setLoadingType(false));
    return () => { alive = false; };
  }, [companyId, invoiceTypeId]);

  const set = (field, value) => setSetting(prev => ({ ...prev, [field]: value }));
  const dirty = useMemo(() => JSON.stringify(setting) !== JSON.stringify(saved), [setting, saved]);

  // ── الحفظ ──
  const handleSave = async () => {
    if (saving) return;
    if (invoiceTypeId == null) {
      toast.error(tr("programSettings.messages.selectInvoiceTypeFirst", "الرجاء اختيار نوع الفاتورة أولاً"));
      return;
    }
    setSaving(true);
    const { id, ...rest } = setting;
    const payload = { ...rest, companyId, invoiceTypeId };
    try {
      if (id) {
        await axios.put(`/ProgramSettings/${id}`, payload);
        setSaved(setting);
        toast.success(tr("programSettings.messages.updateSuccess", "تم التحديث بنجاح"));
      } else {
        const res = await axios.post("/ProgramSettings", payload);
        const next = { ...setting, id: res.data?.id ?? null };
        setSetting(next);
        setSaved(next);
        toast.success(tr("programSettings.messages.createSuccess", "تم الحفظ بنجاح"));
      }
    } catch (err) {
      console.error("❌ ProgramSettings save:", err.response?.status, err.response?.data);
      toast.error(`${tr("programSettings.messages.saveFailed", "فشل الحفظ")}${err.response?.status ? ` (HTTP ${err.response.status})` : ""}`);
    } finally {
      setSaving(false);
    }
  };

  // Ctrl+S
  const saveRef = useRef(handleSave);
  saveRef.current = handleSave;
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") { e.preventDefault(); saveRef.current(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ── حقول مساعدة ──
  const numberField = (field, label, { min = 0, max = 10, adornment } = {}) => (
    <TextField
      size="small" fullWidth type="number" label={label}
      value={setting[field] ?? ""}
      onChange={e => {
        const v = e.target.value === "" ? "" : Math.max(min, Math.min(max, parseInt(e.target.value, 10) || 0));
        set(field, v);
      }}
      onBlur={() => setting[field] === "" && set(field, min)}
      inputProps={{ min, max, inputMode: "numeric" }}
      InputProps={adornment ? { startAdornment: <InputAdornment position="start">{adornment}</InputAdornment> } : undefined}
      sx={{ ...fieldSx, "& input": { direction: "ltr", textAlign: dir === "rtl" ? "right" : "left" } }}
    />
  );

  const lookupField = (field, label, options) => {
    const value = options.find(o => o.id === setting[field]) || null;
    return (
      <Autocomplete
        size="small"
        options={options}
        value={value}
        onChange={(_, v) => set(field, v ? v.id : null)}
        getOptionLabel={nameOf}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        noOptionsText={tr("common.noResults", "لا توجد نتائج")}
        renderInput={p => <TextField {...p} label={label} placeholder={tr("programSettings.none", "بدون")} sx={fieldSx} />}
      />
    );
  };

  const TOGGLES = [
    { field: "isVatExempt", label: tr("programSettings.vatExempt", "بدون ضريبة"), warn: true,
      desc: tr("programSettings.desc.vatExempt", "فواتير هذا النوع بلا ضريبة قيمة مضافة — تُصفَّر نسبة الضريبة لكل الأصناف عند الإنشاء") },
    { field: "isPriceIncludesVAT", label: tr("programSettings.priceIncludesVAT", "السعر شامل الضريبة"),
      desc: tr("programSettings.desc.priceIncludesVAT", "تُدخل أسعار الأصناف شاملة الضريبة، ويُستخرج المبلغ قبل الضريبة تلقائيًا") },
    { field: "autoApproveInvoices", label: tr("programSettings.autoApproveInvoices", "اعتماد الفاتورة تلقائيًا"),
      desc: tr("programSettings.desc.autoApproveInvoices", "تُعتمد الفاتورة فور حفظها فتؤثر مباشرة على المخزون والقيود") },
    { field: "autoPrintAfterSave", label: tr("programSettings.autoPrintAfterSave", "الطباعة التلقائية بعد الحفظ"),
      desc: tr("programSettings.desc.autoPrintAfterSave", "تفتح نافذة الطباعة مباشرة بعد حفظ الفاتورة") },
    { field: "allowNegativeSales", label: tr("programSettings.allowNegativeSales", "السماح بالبيع بالسالب"), warn: true,
      desc: tr("programSettings.desc.allowNegativeSales", "البيع حتى لو الكمية غير متوفرة — تصبح تكلفة هذه المبيعات تقديرية في تقارير الأرباح") },
  ];

  const currentType = lists.invoiceTypes.find(x => x.invoiceTypeId === invoiceTypeId);
  const isNew = !setting.id;
  const loading = loadingLists || loadingType;

  return (
    <ThemeProvider theme={indigoTheme}>
    <Box sx={{
      "& .MuiOutlinedInput-root:hover fieldset": { borderColor: "#4F46E5" },
      "& .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: "#4F46E5" },
      "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: "0 0 0 3px rgba(79,70,229,.12)" },
      "& .MuiInputLabel-root.Mui-focused": { color: "#4F46E5" },
    }}>
    <ReportPage
      brand={INDIGO_BRAND}
      title={tr("programSettings.title", "إعدادات البرنامج")}
      icon={<SettingsOutlinedIcon />}
      badges={[
        ...(currentType ? [{ label: nameOf(currentType), icon: <ReceiptLongOutlinedIcon /> }] : []),
        dirty
          ? { label: tr("programSettings.unsaved", "تغييرات غير محفوظة"), icon: <FiberManualRecordIcon sx={{ color: "#FBBF24" }} /> }
          : { label: tr("programSettings.allSaved", "كل التغييرات محفوظة"), icon: <CheckCircleOutlineIcon /> },
      ]}
      actions={[
        { label: tr("common.back", "رجوع"), icon: <ArrowForwardIcon />, onClick: () => navigate(-1) },
        {
          label: saving ? tr("programSettings.saving", "جاري الحفظ...") : tr("programSettings.saveSettings", "حفظ الإعدادات"),
          icon: saving ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />,
          onClick: handleSave, primary: true, disabled: saving || loading || invoiceTypeId == null,
        },
      ]}
    >
      {/* اختيار نوع الفاتورة */}
      <Paper elevation={0} sx={{
        mb: 1.5, p: { xs: 1.5, sm: 2 }, borderRadius: "14px", border: `1px solid ${BORDER}`,
        display: "grid", gap: 1.5, alignItems: "center",
        gridTemplateColumns: { xs: "1fr", md: "minmax(0,360px) 1fr" },
      }}>
        <TextField
          select size="small" fullWidth
          label={tr("programSettings.invoiceType", "نوع الفاتورة")}
          value={invoiceTypeId ?? ""}
          onChange={e => {
            if (dirty && !window.confirm(tr("programSettings.confirmSwitch", "لديك تغييرات غير محفوظة. هل تريد الانتقال لنوع آخر بدون حفظ؟"))) return;
            setInvoiceTypeId(Number(e.target.value));
          }}
          disabled={loadingLists}
          sx={{ ...fieldSx, "& .MuiSelect-select": { fontWeight: 800, color: BRAND.primary } }}
        >
          {lists.invoiceTypes.map(o => <MenuItem key={o.invoiceTypeId} value={o.invoiceTypeId}>{nameOf(o)}</MenuItem>)}
        </TextField>
        <Typography sx={{ fontSize: 13, color: "#64748b" }}>
          {isNew && !loading
            ? <Chip size="small" label={tr("programSettings.noSettingsForType", "لا توجد إعدادات محفوظة لهذا النوع — ستُنشأ عند الحفظ")}
                sx={{ bgcolor: "#FEF3C7", color: "#B45309", fontWeight: 700, height: "auto", py: .4, "& .MuiChip-label": { whiteSpace: "normal" } }} />
            : tr("programSettings.typeHint", "كل نوع فاتورة له إعداداته الخاصة. اختر النوع ثم عدّل واحفظ (Ctrl + S).")}
        </Typography>
      </Paper>

      {loading ? (
        <Box sx={{ display: "grid", placeItems: "center", minHeight: 260 }}><CircularProgress /></Box>
      ) : (
        <Box sx={{ display: "grid", gap: 1.5, alignItems: "start", gridTemplateColumns: { xs: "1fr", lg: "minmax(0,1fr) 380px" } }}>
          {/* ═════ العمود الرئيسي ═════ */}
          <Box sx={{ display: "grid", gap: 1.5, minWidth: 0 }}>
            <Section title={tr("programSettings.basicSettings", "الإعدادات الأساسية")} icon={<DisplaySettingsOutlinedIcon />}>
              <Grid2>
                {numberField("decimalPlaces", tr("programSettings.decimalPlaces", "عدد المنازل العشرية"), { min: 0, max: 4, adornment: <Typography sx={{ fontSize: 12, fontFamily: "monospace", color: "#94a3b8" }}>.00</Typography> })}
                {numberField("printCopies", tr("programSettings.printCopies", "عدد نسخ الطباعة"), { min: 1, max: 10, adornment: <PrintOutlinedIcon sx={{ fontSize: 18, color: "#94a3b8" }} /> })}
              </Grid2>
            </Section>

            <Section title={tr("programSettings.defaultSettings", "القيم الافتراضية")} icon={<StoreOutlinedIcon />} color={BRAND.secondary}>
              <Grid2>
                {lookupField("defaultCustomerId", tr("programSettings.defaultCustomer", "العميل الافتراضي"), lists.customers)}
                {lookupField("defaultPaymentMethodId", tr("programSettings.defaultPaymentMethod", "طريقة الدفع الافتراضية"), lists.payments)}
                {lookupField("defaultSafeId", tr("programSettings.defaultSafe", "الصندوق الافتراضي"), lists.safes)}
                {lookupField("defaultBankId", tr("programSettings.defaultBank", "البنك الافتراضي"), lists.banks)}
                {lookupField("defaultStockId", tr("programSettings.defaultStock", "المخزن الافتراضي"), lists.stocks)}
                {lookupField("defaultTaxTypeId", tr("programSettings.defaultTaxType", "نوع الضريبة الافتراضي"), lists.taxes)}
              </Grid2>
            </Section>

            <Section
              title={tr("programSettings.printerSettings", "الطابعات")}
              icon={<PrintOutlinedIcon />} color="#2563EB"
              action={
                <Button size="small" variant="text" startIcon={<PaletteOutlinedIcon />} onClick={() => navigate("/print-layout")}
                  sx={{ fontWeight: 700, color: BRAND.primary }}>
                  {tr("programSettings.openprintDesigner", "مصمم الطباعة")}
                </Button>
              }
            >
              <Box sx={{ display: "grid", gap: 1.5 }}>
                <Autocomplete
                  size="small" freeSolo
                  options={lists.printers}
                  value={setting.defaultPrinterName || null}
                  onChange={(_, v) => set("defaultPrinterName", v || "")}
                  onInputChange={(_, v, reason) => reason === "input" && set("defaultPrinterName", v)}
                  renderInput={p => (
                    <TextField {...p} label={tr("programSettings.defaultPrinter", "الطابعة الافتراضية")}
                      placeholder={tr("programSettings.selectPrinter", "اختر طابعة")} sx={fieldSx} />
                  )}
                />
                <Box sx={{ display: "grid", gap: 1.5, alignItems: "start", gridTemplateColumns: { xs: "1fr", sm: "minmax(0,1fr) 200px" } }}>
                  <Autocomplete
                    size="small" multiple filterSelectedOptions
                    options={lists.printers.filter(p => p !== setting.defaultPrinterName)}
                    value={setting.secondaryPrinterNames || []}
                    onChange={(_, v) => {
                      const max = Number(setting.allowedSecondaryPrinterCount) || 0;
                      if (v.length > max) {
                        toast.warning(tr("programSettings.messages.maxSecondaryPrinters", `يمكنك اختيار حتى ${max} طابعات فرعية فقط`, { count: max }));
                        return;
                      }
                      set("secondaryPrinterNames", v);
                    }}
                    noOptionsText={tr("programSettings.noPrinters", "لا توجد طابعات أخرى")}
                    renderInput={p => (
                      <TextField {...p} label={tr("programSettings.secondaryPrinters", "طابعات فرعية")}
                        helperText={tr("programSettings.printersCount", `${(setting.secondaryPrinterNames || []).length} من ${setting.allowedSecondaryPrinterCount || 0}`, { count: (setting.secondaryPrinterNames || []).length, max: setting.allowedSecondaryPrinterCount || 0 })} sx={fieldSx} />
                    )}
                  />
                  {numberField("allowedSecondaryPrinterCount", tr("programSettings.allowedSecondaryPrinterCount", "الحد الأقصى للطابعات الفرعية"), { min: 0, max: 10 })}
                </Box>
                {!lists.printers.length && (
                  <Alert severity="info" sx={{ borderRadius: "12px", py: 0 }}>
                    {tr("programSettings.printersUnavailable", "لم يتم العثور على طابعات من الخادم — يمكنك كتابة اسم الطابعة يدويًا")}
                  </Alert>
                )}
              </Box>
            </Section>
          </Box>

          {/* ═════ خيارات التشغيل ═════ */}
          <Section
            title={tr("programSettings.toggleSettings", "خيارات التشغيل")} icon={<ToggleOnOutlinedIcon />} color="#D97706"
            sx={{ position: { lg: "sticky" }, top: { lg: 16 } }}
          >
            <Box sx={{ mx: { xs: -1.5, sm: -2 }, my: { xs: -1.5, sm: -2 } }}>
              {TOGGLES.map(({ field, label, desc, warn }) => {
                const off = field === "isPriceIncludesVAT" && !!setting.isVatExempt;  // لا معنى له بدون ضريبة
                return (
                <Box
                  key={field}
                  component="label"
                  sx={{
                    ...(off && { opacity: .5, pointerEvents: "none" }),
                    display: "flex", alignItems: "flex-start", gap: 1.5, px: 2, py: 1.5, cursor: "pointer",
                    borderBottom: `1px solid #f1f5f9`, "&:last-of-type": { borderBottom: "none" },
                    "&:hover": { bgcolor: "#fafbfc" },
                    ...(warn && setting[field] && { bgcolor: "#FFFBEB", "&:hover": { bgcolor: "#FEF3C7" } }),
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: 14, color: "#0f172a" }}>{label}</Typography>
                    <Typography sx={{ fontSize: 12.5, color: warn && setting[field] ? "#B45309" : "#64748b", mt: .25, lineHeight: 1.6 }}>{desc}</Typography>
                  </Box>
                  <Switch
                    checked={!!setting[field] && !off}
                    disabled={off}
                    onChange={e => {
                      set(field, e.target.checked);
                      if (field === "isVatExempt" && e.target.checked) set("isPriceIncludesVAT", false);
                    }}
                    inputProps={{ "aria-label": label }}
                    sx={{ mt: -.5 }}
                  />
                </Box>
                );
              })}
            </Box>
          </Section>
        </Box>
      )}
    </ReportPage>
    </Box>
    </ThemeProvider>
  );
}